import React, { createContext, FC, PropsWithChildren, useCallback, useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToastRoot } from 'hooks/usePortalRoot/usePortalRoot';
import { toastZIndex } from 'style/styled-components/cssGlobalVariables';

export type TransitionState = 'entering' | 'entered' | 'exiting' | 'exited';

export type ToastPlacement = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export type ToastProps = {
    transitionState: TransitionState;
    transitionDuration: number;
    placement: ToastPlacement;
    children: React.ReactNode;
    onDismiss: () => void;
};

type ToastEntry = {
    id: string;
    content: React.ReactNode;
    autoDismiss: boolean;
    transitionState: TransitionState;
    callback?: () => void;
};

type AddToastOptions = {
    appearance?: string;
    autoDismiss?: boolean;
};

type ToastContextValue = {
    addToast: (content: React.ReactNode, options?: AddToastOptions, callback?: () => void) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const TRANSITION_DURATION = 220;
const AUTO_DISMISS_TIMEOUT = 5000;

function getPlacementStyle(placement: ToastPlacement): React.CSSProperties {
    const [vPos, hPos] = placement.split('-');
    return {
        top: vPos === 'top' ? 0 : undefined,
        bottom: vPos === 'bottom' ? 0 : undefined,
        left: hPos === 'left' ? 0 : hPos === 'center' ? '50%' : undefined,
        right: hPos === 'right' ? 0 : undefined,
    };
}

type ToastProviderProps = PropsWithChildren<{
    components?: { Toast?: React.ComponentType<ToastProps> };
    placement?: ToastPlacement;
    autoDismissTimeout?: number;
}>;

export const ToastProvider: FC<ToastProviderProps> = ({
    children,
    components,
    placement = 'top-right',
    autoDismissTimeout = AUTO_DISMISS_TIMEOUT,
}) => {
    const toastRoot = useToastRoot();
    const [toasts, setToasts] = useState<ToastEntry[]>([]);
    const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const ToastComponent = components?.Toast;

    const dismissed = useRef<Record<string, true>>({});

    const handleDismiss = useCallback((id: string) => {
        if (dismissed.current[id]) return;
        dismissed.current[id] = true;

        if (timers.current[id]) {
            clearTimeout(timers.current[id]);
            delete timers.current[id];
        }

        let callback: (() => void) | undefined;

        setToasts((prev) => {
            const toast = prev.find((t) => t.id === id);
            callback = toast?.callback;
            return prev.map((t) => (t.id === id ? { ...t, transitionState: 'exiting' } : t));
        });

        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
            delete dismissed.current[id];
        }, TRANSITION_DURATION);

        if (callback) callback();
    }, []);

    const addToast = useCallback(
        (content: React.ReactNode, options?: AddToastOptions, callback?: () => void) => {
            const id = `toast-${Date.now()}-${Math.random()}`;
            const autoDismiss = options?.autoDismiss ?? true;

            setToasts((prev) => [...prev, { id, content, autoDismiss, transitionState: 'entering', callback }]);

            // entering → entered
            setTimeout(() => {
                setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, transitionState: 'entered' } : t)));
            }, 10);

            if (autoDismiss) {
                timers.current[id] = setTimeout(() => {
                    handleDismiss(id);
                }, autoDismissTimeout);
            }
        },
        [autoDismissTimeout, handleDismiss]
    );

    const containerStyle: React.CSSProperties = {
        position: 'fixed',
        zIndex: toastZIndex,
        ...getPlacementStyle(placement),
    };

    return (
        <ToastContext.Provider value={{ addToast }}>
            {children}
            {createPortal(
                <div className="react-toast-notifications__container" style={containerStyle}>
                    {toasts.map((toast) =>
                        ToastComponent ? (
                            <ToastComponent
                                key={toast.id}
                                transitionState={toast.transitionState}
                                transitionDuration={TRANSITION_DURATION}
                                placement={placement}
                                onDismiss={() => handleDismiss(toast.id)}
                            >
                                {toast.content}
                            </ToastComponent>
                        ) : null
                    )}
                </div>,
                toastRoot
            )}
        </ToastContext.Provider>
    );
};

export const useToasts = (): ToastContextValue => {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToasts must be used within a ToastProvider');
    return ctx;
};
